import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/navbar/navbar';
import Footer from '../components/footer/footer';
import './Pricing.css';
import SEO from '../components/SEO';

const SERVICES = {
  rides: {
    title: "9tro Ride",
    subtitle: "Cab booking & travel options",
    icon: "🚗",
    options: [
      { name: "Classic Cab", baseFare: 1000, perKm: 250, desc: "Sleek compact cars for fast daily commutes.", capacity: "4 passengers", icon: "🚗" },
      { name: "Premium Sedan", baseFare: 1800, perKm: 400, desc: "Spacious, air-conditioned sedans for corporate travels.", capacity: "4 passengers", icon: "✨" },
      { name: "VIP SUV", baseFare: 3500, perKm: 650, desc: "Luxury SUVs for maximum comfort and style.", capacity: "6 passengers", icon: "🚙" }
    ]
  },
  deliveries: {
    title: "9tro Delivery",
    subtitle: "On-demand logistics & cargo",
    icon: "📦",
    options: [
      { name: "Eco Bicycle", baseFare: 250, perKm: 80, desc: "Green and fast delivery for documents or letters.", capacity: "Max 5kg", icon: "🚲" },
      { name: "Motorcycle", baseFare: 400, perKm: 120, desc: "Quick delivery for grocery, clothing, and small boxes.", capacity: "Max 20kg", icon: "🏍️" },
      { name: "Express Truck", baseFare: 1500, perKm: 300, desc: "Heavy duty trucks for moving furniture and wholesale goods.", capacity: "Max 500kg", icon: "🚚" }
    ]
  },
  eats: {
    title: "9tro Eat",
    subtitle: "Insulated food delivery rates",
    icon: "🍕",
    options: [
      { name: "Restaurant Delivery", baseFare: 800, perKm: 0, desc: "Flat rate delivery from our restaurant partners.", capacity: "Hot bag insulated", icon: "🍔" },
      { name: "Private Chef Delivery", baseFare: 1200, perKm: 0, desc: "Special handles for gourmet meals cooked by private chefs.", capacity: "Premium boxes", icon: "👨‍🍳" },
      { name: "Priority Express", baseFare: 1500, perKm: 0, desc: "Direct route delivery to guarantee fastest arrival time.", capacity: "Direct dispatch", icon: "🚀" }
    ]
  },
  partners: {
    title: "9tro Partner Plans",
    subtitle: "Subscriptions & commissions",
    icon: "💼",
    options: [
      { name: "Driver Weekly Subscription", baseFare: 3500, perKm: 0, desc: "Weekly unlimited matching for 9tro partner drivers.", capacity: "Zero commissions", feeType: "Subscription", feeValue: "₦3,500/week", icon: "🚗" },
      { name: "Driver Monthly Subscription", baseFare: 12000, perKm: 0, desc: "Save 15% with our monthly driver subscription plan.", capacity: "Zero commissions", feeType: "Subscription", feeValue: "₦12,000/month", icon: "✨" },
      { name: "Vendor Store Subscription", baseFare: 15000, perKm: 0, desc: "Monthly listing and order dispatching for stores.", capacity: "Billed monthly", feeType: "Subscription", feeValue: "₦15,000/month", icon: "🏪" },
      { name: "Private Chef Plan", baseFare: 0, perKm: 0, desc: "List culinary menus with a simple order commission.", capacity: "Sales commission", feeType: "Commission", feeValue: "5% per order", icon: "👨‍🍳" }
    ]
  }
};

const FAQS = [
  { q: "How is the distance-based fare calculated?", a: "Distance fares are calculated using the shortest routing path provided by Google Maps. Total cost = Base Fare + (Distance in km × Rate per km)." },
  { q: "Does 9tro Ride have surge pricing?", a: "During times of high demand (rush hours, heavy rain, holiday events), prices may increase slightly to match courier availability. You will always see the exact price upfront before booking." },
  { q: "What payment methods are supported?", a: "We support cash on delivery/arrival, card payments via Paystack, bank transfers, and our secure built-in 9tro Wallet system." },
  { q: "Are package deliveries insured?", a: "Yes, all premium deliveries are insured up to ₦100,000. For valuable items, please specify high-value tags when submitting request details." }
];

function Pricing() {
  const navigate = useNavigate();
  const [selectedService, setSelectedService] = useState('rides');
  
  // Fare Estimator Live State
  const [calcService, setCalcService] = useState('rides');
  const [calcOptionName, setCalcOptionName] = useState('Classic Cab');
  const [calcDistance, setCalcDistance] = useState(15); // Default 15km
  const [estimatedTotal, setEstimatedTotal] = useState(0);

  // Driver Savings Live State
  const [dailyTrips, setDailyTrips] = useState(8);
  const [avgFare, setAvgFare] = useState(3500);

  // Toggleable FAQ state
  const [openFaqIndex, setOpenFaqIndex] = useState(null);

  // Sync Option Name when Service changes in Calculator
  const handleCalcServiceChange = (serviceKey) => {
    setCalcService(serviceKey);
    setCalcOptionName(SERVICES[serviceKey].options[0].name);
  };

  // Live calculation effect
  useEffect(() => {
    const serviceData = SERVICES[calcService];
    const option = serviceData.options.find(opt => opt.name === calcOptionName);
    if (option) {
      if (option.feeType) {
        setEstimatedTotal(option.baseFare);
      } else {
        setEstimatedTotal(option.baseFare + (calcDistance * option.perKm));
      }
    }
  }, [calcService, calcOptionName, calcDistance]);

  // Savings Math
  const weeklyTraditionalCut = dailyTrips * 7 * avgFare * 0.25;
  const weekly9troCost = 3500;
  const weeklyNetSavings = Math.max(0, weeklyTraditionalCut - weekly9troCost);
  const monthlyNetSavings = weeklyNetSavings * 4;

  return (
    <div className="pricing-page-wrapper">
      <SEO 
        title="Transparent Pricing & Cost Estimator"
        description="Check real-time pricing plans for 9tro Ride hailing, package delivery, and private chef meals. Estimate your trip fare instantly."
        url="https://www.9troride.com/pricing"
      />
      <Navbar showSubNavbar={false} />
      
      {/* Background Decorative Blur Blobs */}
      <div className="bg-blob blob-1" />
      <div className="bg-blob blob-2" />

      {/* Hero Section */}
      <section className="pricing-hero">
        <div className="pricing-hero-container">
          <span className="hero-tag">Rates & Live Estimator</span>
          <h1>Flat rates. Zero commission matches. Clear plans.</h1>
          <p>No platform brokers, no hidden cuts. Compute your savings and estimate fares dynamically.</p>
        </div>
      </section>

      {/* Calculator Section */}
      <section className="estimator-section">
        <div className="estimator-container">
          <div className="estimator-content">
            <span className="section-mini-label">Fare Calculator</span>
            <h2>Estimate Your Fare</h2>
            <p>Drag the slider to adjust distance and select vehicle options to see live fare updates.</p>
            
            <div className="calc-service-tabs">
              {['rides', 'deliveries', 'eats'].map(key => (
                <button
                  key={key}
                  className={`calc-service-tab ${calcService === key ? 'active' : ''}`}
                  onClick={() => handleCalcServiceChange(key)}
                >
                  <span className="calc-tab-icon">{SERVICES[key].icon}</span>
                  {SERVICES[key].title.split(' ')[1] || SERVICES[key].title}
                </button>
              ))}
            </div>

            <div className="estimator-form">
              <div className="form-group">
                <label>Select Option</label>
                <div className="options-selector-row">
                  {SERVICES[calcService].options.map(opt => (
                    <button
                      key={opt.name}
                      type="button"
                      className={`option-capsule ${calcOptionName === opt.name ? 'active' : ''}`}
                      onClick={() => setCalcOptionName(opt.name)}
                    >
                      <span className="option-capsule-icon">{opt.icon}</span>
                      <span className="option-capsule-name">{opt.name}</span>
                    </button>
                  ))}
                </div>
              </div>

              {SERVICES[calcService].options[0].perKm > 0 && (
                <div className="form-group distance-slider-group">
                  <div className="distance-label-row">
                    <label>Estimated Distance</label>
                    <span className="live-distance-value">{calcDistance} <span>km</span></span>
                  </div>
                  <input 
                    type="range" 
                    min="1" 
                    max="100" 
                    step="1"
                    value={calcDistance}
                    onChange={(e) => setCalcDistance(parseInt(e.target.value))}
                    className="custom-range-slider"
                  />
                  <div className="slider-range-labels">
                    <span>1 km</span>
                    <span>50 km</span>
                    <span>100 km</span>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="estimator-result-pane">
            <div className="result-display">
              <span className="result-icon-badge">{SERVICES[calcService].options.find(o => o.name === calcOptionName)?.icon}</span>
              <h4>{calcOptionName} Estimate</h4>
              
              <div className="result-price-box">
                <span className="price-label">Estimated Total</span>
                <div className="price-amount-row">
                  <span className="currency-symbol">₦</span>
                  <span className="price-number">{estimatedTotal.toLocaleString()}</span>
                </div>
              </div>

              <div className="result-breakdown">
                <div className="breakdown-line">
                  <span>Base Fare</span>
                  <span>₦{SERVICES[calcService].options.find(o => o.name === calcOptionName)?.baseFare.toLocaleString()}</span>
                </div>
                {SERVICES[calcService].options[0].perKm > 0 && (
                  <div className="breakdown-line">
                    <span>Distance Fare ({calcDistance} km)</span>
                    <span>₦{(SERVICES[calcService].options.find(o => o.name === calcOptionName)?.perKm * calcDistance).toLocaleString()}</span>
                  </div>
                )}
                {SERVICES[calcService].options[0].perKm === 0 && (
                  <div className="breakdown-line">
                    <span>Rate Type</span>
                    <span>Flat Delivery Rate</span>
                  </div>
                )}
              </div>
              <p className="result-disclaimer">Fare estimates do not include tool charges or high traffic surcharges. Base prices are standard baseline estimates.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Driver Savings / Subscription Advantage HUD */}
      <section className="subscription-advantage-section">
        <div className="subscription-advantage-container">
          <div className="hud-header">
            <span className="section-mini-label">Interactive Savings HUD</span>
            <h2>Commission vs. Subscription Savings</h2>
            <p>Adjust the sliders to calculate how much weekly and monthly revenue you retain by switching to 9TRORIDE's flat subscription.</p>
          </div>

          <div className="savings-hud-grid">
            <div className="hud-controls-card">
              <h3>Customize Your Business</h3>
              <p className="hud-controls-desc">Enter your average daily bookings and fare amounts to recalculate savings.</p>
              
              <div className="hud-sliders-list">
                <div className="form-group">
                  <div className="slider-label-row">
                    <label>Average Trips / Day</label>
                    <span className="live-slider-value">{dailyTrips} <span>trips</span></span>
                  </div>
                  <input 
                    type="range"
                    min="1"
                    max="25"
                    step="1"
                    value={dailyTrips}
                    onChange={(e) => setDailyTrips(parseInt(e.target.value))}
                    className="custom-range-slider green-thumb"
                  />
                  <div className="slider-range-labels">
                    <span>1 trip</span>
                    <span>12 trips</span>
                    <span>25 trips</span>
                  </div>
                </div>

                <div className="form-group">
                  <div className="slider-label-row">
                    <label>Average Fare per Trip</label>
                    <span className="live-slider-value">₦{avgFare.toLocaleString()}</span>
                  </div>
                  <input 
                    type="range"
                    min="1000"
                    max="15000"
                    step="500"
                    value={avgFare}
                    onChange={(e) => setAvgFare(parseInt(e.target.value))}
                    className="custom-range-slider green-thumb"
                  />
                  <div className="slider-range-labels">
                    <span>₦1,000</span>
                    <span>₦8,000</span>
                    <span>₦15,000</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="hud-insights-card">
              <div className="insights-headline">
                <span className="insights-lbl">Your Weekly Net Savings</span>
                <div className="savings-large-amount">
                  <span className="cur-symbol">₦</span>
                  <span className="sav-val">{weeklyNetSavings.toLocaleString()}</span>
                </div>
              </div>

              <div className="hud-metrics-row">
                <div className="hud-metric-pill loss">
                  <span className="pill-lbl">Traditional 25% Cut</span>
                  <span className="pill-val">₦{weeklyTraditionalCut.toLocaleString()}/wk</span>
                </div>
                <div className="hud-metric-pill subscription">
                  <span className="pill-lbl">9TRORIDE Fee</span>
                  <span className="pill-val">₦{weekly9troCost.toLocaleString()}/wk</span>
                </div>
              </div>

              <div className="savings-progress-bar-container">
                <div className="progress-bar-labels">
                  <span>9TRORIDE Keep Ratio</span>
                  <span>100%</span>
                </div>
                <div className="progress-bar-track">
                  <div className="progress-bar-fill green" style={{ width: '100%' }}></div>
                </div>
                <div className="progress-bar-labels sub-labels">
                  <span>Traditional Apps Keep Ratio</span>
                  <span>75%</span>
                </div>
                <div className="progress-bar-track">
                  <div className="progress-bar-fill slate" style={{ width: '75%' }}></div>
                </div>
              </div>

              <div className="hud-savings-summary-box">
                <span className="icon">🚀</span>
                <p>By switching, you take home an extra <strong>₦{monthlyNetSavings.toLocaleString()}</strong> every month. That is your money, kept in your pocket.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Grid of All Pricing Plans */}
      <section className="pricing-grid-section">
        <div className="pricing-grid-container">
          <div className="section-header-centered">
            <h2>Detailed Fares & Plans</h2>
            <p>Browse baseline costs and capacity details across all categories.</p>
            
            <div className="grid-switcher-pills">
              {Object.keys(SERVICES).map(key => (
                <button
                  key={key}
                  className={`grid-pill ${selectedService === key ? 'active' : ''}`}
                  onClick={() => setSelectedService(key)}
                >
                  {SERVICES[key].title}
                </button>
              ))}
            </div>
          </div>

          <div className="plans-detailed-grid">
            {SERVICES[selectedService].options.map(opt => (
              <div key={opt.name} className="plan-grid-card">
                <div className="plan-card-header">
                  <span className="plan-emoji">{opt.icon}</span>
                  <h3>{opt.name}</h3>
                  <span className="plan-capacity">{opt.capacity}</span>
                </div>
                <p className="plan-desc">{opt.desc}</p>
                <div className="plan-price-breakdown">
                  {opt.feeType ? (
                    <div className="detail-price-row">
                      <span className="lbl">{opt.feeType}</span>
                      <span className="val highlight">{opt.feeValue}</span>
                    </div>
                  ) : (
                    <>
                      <div className="detail-price-row">
                        <span className="lbl">Base Fare</span>
                        <span className="val">₦{opt.baseFare.toLocaleString()}</span>
                      </div>
                      <div className="detail-price-row">
                        <span className="lbl">Rate per km</span>
                        <span className="val">{opt.perKm > 0 ? `₦${opt.perKm}/km` : 'Flat Rate'}</span>
                      </div>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Accordions */}
      <section className="pricing-faq">
        <div className="pricing-faq-container">
          <h2 className="section-title">Frequently Asked Questions</h2>
          <div className="faq-accordions-wrapper">
            {FAQS.map((faq, idx) => (
              <div 
                key={idx} 
                className={`faq-accordion-card ${openFaqIndex === idx ? 'expanded' : ''}`}
                onClick={() => setOpenFaqIndex(openFaqIndex === idx ? null : idx)}
              >
                <div className="faq-accordion-header">
                  <h3>{faq.q}</h3>
                  <span className="faq-accordion-arrow">{openFaqIndex === idx ? '−' : '+'}</span>
                </div>
                <div className="faq-accordion-body">
                  <p>{faq.a}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="pricing-cta">
        <div className="pricing-cta-container">
          <h2>Ready to request a dispatch or ride?</h2>
          <p>Get fast and secure transit services matching your exact preferences.</p>
          <div className="cta-actions">
            <button className="cta-btn-primary" onClick={() => navigate('/package-delivery')}>Request Delivery</button>
            <button className="cta-btn-secondary" onClick={() => navigate('/')}>Book a Ride</button>
          </div>
        </div>
      </section>
      
      <Footer />
    </div>
  );
}

export default Pricing;
